import { Form, Grid, Label, Radio, Select, Text, TextField } from '@approvalmax/ui/src/components';
import { memo, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { SourceOfFunds } from 'shared/data/walletsKyc/v1';
import { z } from 'zod';

import { schema as companyInfoSchema } from '../../../../companyInfo/resources/schema';
import { schema } from '../../schema';
import { primaryPurposes, sourcesOfFunds } from './StepForm.constants';
import { isInitialCapitalNeeded } from './StepForm.helpers';
import { messages } from './StepForm.messages';

export const StepForm = memo(() => {
    const companyInfoForm = useFormContext<z.infer<typeof companyInfoSchema>>();
    const form = useFormContext<z.infer<typeof schema>>();
    const incorporationDate = companyInfoForm.getValues('businessInformation.incorporationDate');

    const isOtherSourceOfFunds =
        useWatch<z.infer<typeof schema>, 'accountUsage.sourceOfFunds'>({
            name: 'accountUsage.sourceOfFunds',
        }) === SourceOfFunds.other;

    const askForInitialCapital = isInitialCapitalNeeded(incorporationDate);

    useEffect(() => {
        form.setValue('accountUsage.askForInitialCapital', askForInitialCapital);
    }, [askForInitialCapital, form]);

    useEffect(() => {
        if (!isOtherSourceOfFunds) {
            form.setValue('accountUsage.otherSourceOfFunds', undefined);
        }
    }, [isOtherSourceOfFunds, form]);

    return (
        <Form.Part>
            <Text font='label' fontSize='medium' fontWeight='regular'>
                {messages.asPartOfRequirements}
            </Text>

            <Select
                required
                clearable={false}
                name='accountUsage.primaryPurpose'
                qa='primaryPurpose'
                label={messages.primaryPurposeLabel}
                placeholder={messages.primaryPurposePlaceholder}
                items={primaryPurposes}
            />

            <Select
                required
                clearable={false}
                name='accountUsage.sourceOfFunds'
                qa='sourceOfFunds'
                label={messages.sourceOfFundsLabel}
                placeholder={messages.sourceOfFundsPlaceholder}
                items={sourcesOfFunds}
            />

            {askForInitialCapital && (
                <TextField
                    name='accountUsage.initialCapital'
                    qa='initialCapital'
                    label={messages.initialCapital}
                    placeholder={messages.initialCapitalPlaceholder}
                    hint={messages.initialCapitalHelperText}
                    rows={3}
                    multiline
                    required
                />
            )}

            {isOtherSourceOfFunds && (
                <TextField
                    name='accountUsage.otherSourceOfFunds'
                    qa='otherSourceOfFunds'
                    label={messages.otherSourceOfFundsLabel}
                    placeholder={messages.otherSourceOfFundsPlaceholder}
                    hint={messages.otherSourceOfFundsHelperText}
                    rows={3}
                    multiline
                    required
                />
            )}

            <Grid gap={12}>
                <Label required font='body' size='medium' color='midnight80' spacing='0'>
                    {messages.willYouReceive}
                </Label>

                <Radio.Group
                    name='accountUsage.collectionsAccount'
                    qa='collectionsAccount'
                    direction='column'
                    spacing='12'
                >
                    <Radio value='true'>{messages.yes}</Radio>

                    <Radio value='false'>{messages.no}</Radio>
                </Radio.Group>
            </Grid>
        </Form.Part>
    );
});

StepForm.displayName = 'StepForm';
